import { useQueryGetAllBots } from "@apis:bot/bot";
import { useQueryGetAllOrganizations } from "@apis:organization/organization";
import {
  BrandingConfig,
  PageConfig,
  PageKey,
  uiBrandingQueryKey,
  useMutationCreateUiBranding,
  useQueryGetAllUiBrandings,
} from "@apis:ui_branding/ui_branding";
import { SuperDialog } from "@components:SuperDialog";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { Box } from "@mui/material";
import { useToasterStoreActions } from "@stores:global";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BrowserSection } from "./UiBrandingModal/BrowserSection";
import {
  buildBrandingConfig,
  formValuesFromConfig,
} from "./UiBrandingModal/buildBrandingConfig";
import {
  DEFAULT_BACKGROUND,
  DEFAULT_ICON_TYPES,
  DEFAULT_PRIMARY,
  DEFAULT_SECONDARY,
  DEFAULT_SIDEBAR,
  PAGE_KEYS,
} from "./UiBrandingModal/constants";
import { CreateCoreSection } from "./UiBrandingModal/CreateCoreSection";
import { IconsSection } from "./UiBrandingModal/IconsSection";
import { ResetButton } from "./UiBrandingModal/ResetButton";
import { SectionNav } from "./UiBrandingModal/SectionNav";
import { TextSection } from "./UiBrandingModal/TextSection";
import { FormData, SectionKey } from "./UiBrandingModal/types";
import { useFaviconUpload } from "./UiBrandingModal/useFaviconUpload";
import { useLogoUpload } from "./UiBrandingModal/useLogoUpload";

interface UiBrandingCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CreateFormData extends FormData {
  organizationId: string;
  botId: string;
}

const createDefaultFormValues = (): CreateFormData => ({
  organizationId: "",
  botId: "",
  logoBase64: "",
  primaryColor: DEFAULT_PRIMARY,
  secondaryColor: DEFAULT_SECONDARY,
  sidebarColor: DEFAULT_SIDEBAR,
  backgroundColor: DEFAULT_BACKGROUND,
  browserTitle: "",
  faviconUrl: "",
  faviconBase64: "",
  pages: PAGE_KEYS.reduce(
    (acc, page) => ({
      ...acc,
      [page]: {
        iconKey: null,
        customIconSvg: null,
        tooltip: null,
        title: null,
        description: null,
      },
    }),
    {} as Record<PageKey, PageConfig>,
  ),
});

export const UiBrandingCreateModal = ({
  isOpen,
  onClose,
}: UiBrandingCreateModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();

  const { data: organizations, isLoading: isOrgLoading } =
    useQueryGetAllOrganizations();
  const { data: bots, isLoading: isBotLoading } = useQueryGetAllBots(true);
  const { data: existingBrandings, isFetched: existingBrandingsFetched } =
    useQueryGetAllUiBrandings(true);

  const [activeSection, setActiveSection] = useState<SectionKey>("core");
  const [activePage, setActivePage] = useState<PageKey>("chat");
  const [iconTypes, setIconTypes] =
    useState<Record<PageKey, "preset" | "custom">>(DEFAULT_ICON_TYPES);

  const { control, handleSubmit, formState, setValue, reset, watch } =
    useForm<CreateFormData>({
      defaultValues: createDefaultFormValues(),
      mode: "all",
    });

  const selectedOrgId = watch("organizationId");

  const organizationOptions = useMemo(() => {
    if (!organizations) return [];
    return organizations.map((org) => ({ value: org.id, label: org.name }));
  }, [organizations]);

  const botOptions = useMemo(() => {
    if (!selectedOrgId) return [];
    const orgBrandingExists = (existingBrandings ?? []).some(
      (b) => b.organizationId === selectedOrgId && !b.botId,
    );
    const brandedBotIds = new Set(
      (existingBrandings ?? [])
        .filter((b) => b.organizationId === selectedOrgId && b.botId)
        .map((b) => b.botId),
    );
    const options = orgBrandingExists
      ? []
      : [{ value: "", label: t("preferences.uiBranding.entireOrganization") }];
    (bots ?? [])
      .filter(
        (bot) =>
          bot.organization_id === selectedOrgId && !brandedBotIds.has(bot.id),
      )
      .forEach((bot) => options.push({ value: bot.id, label: bot.name }));
    return options;
  }, [selectedOrgId, bots, existingBrandings, t]);

  const { logoPreview, setLogoPreview, handleLogoUpload, handleRemoveLogo } =
    useLogoUpload(setValue);

  const {
    faviconPreview,
    setFaviconPreview,
    handleFaviconUpload,
    handleRemoveFavicon,
  } = useFaviconUpload(setValue);

  const pages = watch("pages");
  const faviconUrl = watch("faviconUrl");
  const selectedBotId = watch("botId");

  const applyBrandingToForm = (
    config: BrandingConfig | null | undefined,
    botId: string,
  ) => {
    reset({
      ...formValuesFromConfig(config),
      organizationId: selectedOrgId,
      botId,
    });

    setLogoPreview(config?.logoBase64 ?? null);
    setFaviconPreview(config?.faviconBase64 ?? null);

    const newIconTypes = { ...DEFAULT_ICON_TYPES };
    PAGE_KEYS.forEach((page) => {
      if (config?.pages?.[page]?.customIconSvg) {
        newIconTypes[page] = "custom";
      }
    });

    setIconTypes(newIconTypes);
  };

  // Changing the organization clears the bot and resets the design to defaults
  useEffect(() => {
    applyBrandingToForm(undefined, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  // Selecting a bot prefills the design from the org's branding
  useEffect(() => {
    if (!selectedBotId || !existingBrandingsFetched) return;

    const orgConfig = existingBrandings?.find(
      (b) => b.organizationId === selectedOrgId && !b.botId,
    )?.config;

    applyBrandingToForm(orgConfig, selectedBotId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBotId, existingBrandingsFetched]);

  // "Entire organization" is only a valid target when the org isn't already
  // branded at the org level (i.e. the option is present in botOptions).
  const entireOrgAvailable = botOptions.some((opt) => opt.value === "");
  // A valid target is: entire org (botId "") when available, or a bot.
  const hasValidTarget =
    existingBrandingsFetched &&
    (selectedBotId ? true : entireOrgAvailable && !!selectedOrgId);

  const createMutation = useMutationCreateUiBranding({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        variant: "success",
        message: t("preferences.uiBranding.created"),
      });
      await invalidateQueries.run([
        { queryKey: uiBrandingQueryKey.all(true).queryKey },
        { queryKey: uiBrandingQueryKey.all(false).queryKey },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        variant: "error",
        message: t("preferences.uiBranding.createFailed"),
      });
    },
  });

  const handleReset = () => {
    reset(createDefaultFormValues());
    setLogoPreview(null);
    setFaviconPreview(null);
    setIconTypes(DEFAULT_ICON_TYPES);
  };

  const handleIconTypeChange = (type: "preset" | "custom") => {
    setIconTypes((prev) => ({ ...prev, [activePage]: type }));
    if (type === "preset") {
      setValue(`pages.${activePage}.customIconSvg`, null, {
        shouldDirty: true,
      });
    } else {
      setValue(`pages.${activePage}.iconKey`, null, { shouldDirty: true });
    }
  };

  const onSubmit = async (data: CreateFormData) => {
    await createMutation.mutateAsync({
      organizationId: data.organizationId,
      botId: data.botId || null,
      config: buildBrandingConfig(data),
    });
  };

  const isSubmitting = createMutation.isPending;

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={t("preferences.uiBranding.createTitle")}
      isLoading={isOrgLoading}
      sx={{
        "& .MuiDialog-paper": {
          width: "40rem",
          maxWidth: "95vw",
          borderRadius: 3,
          maxHeight: "none",
          overflow: "hidden",
        },
      }}
      leftAction={<ResetButton onClick={handleReset} />}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: onClose,
          disabled: isSubmitting,
        },
        primary: {
          label: isSubmitting
            ? t("preferences.uiBranding.creating")
            : t("preferences.uiBranding.create"),
          onClick: handleSubmit(onSubmit),
          disabled: !formState.isValid || isSubmitting || !hasValidTarget,
        },
      }}
    >
      <Box>
        <SectionNav
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <Box>
          {activeSection === "core" && (
            <CreateCoreSection
              organizationOptions={organizationOptions}
              isOrgLoading={isOrgLoading}
              botOptions={botOptions}
              isBotLoading={isBotLoading}
              botDisabled={!selectedOrgId}
              logoPreview={logoPreview}
              onLogoUpload={handleLogoUpload}
              onLogoRemove={handleRemoveLogo}
              control={control}
              errors={formState.errors}
            />
          )}
          {activeSection === "browser" && (
            <BrowserSection
              faviconPreview={faviconPreview}
              onFaviconUpload={handleFaviconUpload}
              onFaviconRemove={handleRemoveFavicon}
              faviconUrl={faviconUrl}
              setValue={setValue}
              control={control}
            />
          )}
          {activeSection === "icons" && (
            <IconsSection
              activePage={activePage}
              onPageChange={setActivePage}
              iconType={iconTypes[activePage]}
              onIconTypeChange={handleIconTypeChange}
              pages={pages}
              control={control}
              setValue={setValue}
            />
          )}
          {activeSection === "text" && (
            <TextSection
              activePage={activePage}
              onPageChange={setActivePage}
              control={control}
            />
          )}
        </Box>
      </Box>
    </SuperDialog>
  );
};
